import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist'
import type { TextItem } from 'pdfjs-dist/types/src/display/api'
import { supabase } from './supabase'
import { base } from './db'
import { ouvrirPlan } from './plans' // configure aussi le moteur PDF
import { decouperEtTeleverser } from './decoupage'
import { televerserGros } from './televersement'
import type { DocumentChantier } from './types'
import {
  assemblerLignes,
  extraits,
  normaliser,
  occurrences,
  sectionDePage,
  sectionsDuDocument,
  termes,
  type LignePage,
  type Morceau,
  type SectionPage
} from './devisTexte'

/**
 * Onglet Devis : le cahier des charges et ses addenda.
 *
 * À l'import (sur l'ordinateur d'un administrateur) :
 *  1. le PDF est envoyé tel quel ;
 *  2. le texte de chaque page est lu et enregistré, avec la section
 *     MasterFormat de la page (07 21 00…) : c'est ce qui permet la
 *     recherche plein texte sur tout le devis ;
 *  3. les pages sont enregistrées séparément, comme pour les plans : au
 *     chantier, le téléphone ne télécharge que les pages ouvertes.
 *
 * La recherche se fait sur le serveur (réseau requis). Les pages déjà
 * ouvertes restent consultables sans réseau.
 */

export interface DocumentDevis extends DocumentChantier {
  addenda_numero?: string | null
  date_emission?: string | null
  devis_id?: string | null
  texte_extrait_le?: string | null
  importe_le?: string
}

export interface PageSectionDevis {
  document_id: string
  page: number
  section_code: string | null
  section_titre: string | null
  section_source: 'auto' | 'manuel'
}

/** Suite de pages consécutives d'un document qui portent la même section. */
export interface PlageSection {
  document_id: string
  code: string | null
  titre: string | null
  debut: number
  fin: number
  manuel: boolean
}

export interface Favori {
  code: string
  titre: string | null
}

// ---------------------------------------------------------------------
// Lecture
// ---------------------------------------------------------------------

export async function chargerDocumentsDevis(chantierId: string): Promise<DocumentDevis[]> {
  const { data, error } = await supabase
    .from('documents')
    .select('*')
    .eq('chantier_id', chantierId)
    .in('type', ['devis', 'addenda'])
    .is('supprime_le', null)
    .order('nom')
  if (error) throw error
  return (data as DocumentDevis[]) ?? []
}

/** Section de chaque page de tous les documents du devis (sans le texte). */
export async function chargerSectionsPages(chantierId: string): Promise<PageSectionDevis[]> {
  const tout: PageSectionDevis[] = []
  const lot = 1000
  for (let debut = 0; ; debut += lot) {
    const { data, error } = await supabase
      .from('devis_pages')
      .select('document_id, page, section_code, section_titre, section_source, documents!inner(supprime_le)')
      .eq('chantier_id', chantierId)
      .is('documents.supprime_le', null) // pas les documents à la corbeille
      .order('document_id')
      .order('page')
      .range(debut, debut + lot - 1)
    if (error) throw error
    tout.push(
      ...((data ?? []) as (PageSectionDevis & { documents?: unknown })[]).map(({ documents: _d, ...p }) => p)
    )
    if (!data || data.length < lot) break
  }
  return tout
}

/** Regroupe les pages en suites consécutives de même section. */
export function regrouper(pages: PageSectionDevis[]): PlageSection[] {
  const out: PlageSection[] = []
  for (const p of pages) {
    const der = out[out.length - 1]
    if (
      der &&
      der.document_id === p.document_id &&
      der.code === p.section_code &&
      der.titre === p.section_titre &&
      der.fin === p.page - 1
    ) {
      der.fin = p.page
      der.manuel = der.manuel || p.section_source === 'manuel'
    } else {
      out.push({
        document_id: p.document_id,
        code: p.section_code,
        titre: p.section_titre,
        debut: p.page,
        fin: p.page,
        manuel: p.section_source === 'manuel'
      })
    }
  }
  return out
}

// ---------------------------------------------------------------------
// Recherche plein texte
// ---------------------------------------------------------------------

export interface ResultatDevis {
  document_id: string
  page: number
  section_code: string | null
  section_titre: string | null
  nb: number
  extraits: Morceau[][]
}

const LIMITE = 150

/**
 * Pages qui contiennent tous les termes (sans tenir compte des accents ni
 * des majuscules), avec des extraits surlignés.
 */
export async function chercher(
  chantierId: string,
  requete: string
): Promise<{ resultats: ResultatDevis[]; plus: boolean; termes: string[] }> {
  const t = termes(requete)
  if (t.length === 0) return { resultats: [], plus: false, termes: t }
  if (!navigator.onLine) throw new Error('Pas de réseau : la recherche dans le devis demande Internet.')
  let q = supabase
    .from('devis_pages')
    .select('document_id, page, section_code, section_titre, texte, documents!inner(supprime_le)')
    .eq('chantier_id', chantierId)
    .is('documents.supprime_le', null) // pas les documents à la corbeille
  for (const x of t) q = q.like('texte_norm', `%${x}%`)
  const { data, error } = await q.order('document_id').order('page').limit(LIMITE + 1)
  if (error) throw error
  const lignes = (data ?? []) as {
    document_id: string
    page: number
    section_code: string | null
    section_titre: string | null
    texte: string
  }[]
  return {
    termes: t,
    plus: lignes.length > LIMITE,
    resultats: lignes.slice(0, LIMITE).map((l) => ({
      document_id: l.document_id,
      page: l.page,
      section_code: l.section_code,
      section_titre: l.section_titre,
      nb: occurrences(l.texte, t).length,
      extraits: extraits(l.texte, t)
    }))
  }
}

// ---------------------------------------------------------------------
// Sections suivies
// ---------------------------------------------------------------------

export async function chargerFavoris(chantierId: string): Promise<Favori[]> {
  const { data, error } = await supabase
    .from('devis_favoris')
    .select('code, titre')
    .eq('chantier_id', chantierId)
    .order('code')
  if (error) throw error
  return (data as Favori[]) ?? []
}

export async function basculerFavori(chantierId: string, f: Favori, suivre: boolean): Promise<void> {
  if (suivre) {
    const { error } = await supabase
      .from('devis_favoris')
      .upsert({ chantier_id: chantierId, code: f.code, titre: f.titre }, { onConflict: 'chantier_id,code' })
    if (error) throw error
  } else {
    const { error } = await supabase.from('devis_favoris').delete().eq('chantier_id', chantierId).eq('code', f.code)
    if (error) throw error
  }
}

// ---------------------------------------------------------------------
// Correction des sections (administrateur)
// ---------------------------------------------------------------------

/** Donne une section (code, titre) aux pages « debut » à « fin » d'un document. */
export async function corrigerSection(
  documentId: string,
  debut: number,
  fin: number,
  code: string | null,
  titre: string | null
): Promise<void> {
  const { error } = await supabase
    .from('devis_pages')
    .update({ section_code: code, section_titre: titre, section_source: 'manuel' })
    .eq('document_id', documentId)
    .gte('page', debut)
    .lte('page', fin)
  if (error) throw error
}

/** Met un code saisi à la main en forme « 07 21 00 ». */
export function formaterCode(saisie: string): string | null {
  const chiffres = saisie.replace(/\D/g, '')
  if (chiffres.length === 6) return `${chiffres.slice(0, 2)} ${chiffres.slice(2, 4)} ${chiffres.slice(4)}`
  if (chiffres.length === 0) return null
  return saisie.trim()
}

// ---------------------------------------------------------------------
// Import et préparation
// ---------------------------------------------------------------------

export type Etape = 'envoi' | 'texte' | 'pages'

export interface InfosImport {
  type: 'devis' | 'addenda'
  addenda_numero?: string | null
  date_emission?: string | null
  devis_id?: string | null
}

/** Envoie le PDF, crée la fiche du document, puis le prépare (texte et pages). */
export async function importerDocumentDevis(
  chantierId: string,
  fichier: File,
  infos: InfosImport,
  importePar: string | null,
  progression: (etape: Etape, fraction: number) => void,
  annule: () => boolean
): Promise<{ doc: DocumentDevis; sansTexte: boolean }> {
  const chemin = `${chantierId}/${crypto.randomUUID()}.pdf`
  progression('envoi', 0)
  if (fichier.size > 6 * 1024 * 1024) {
    await televerserGros('documents', chemin, fichier, (f) => progression('envoi', f))
  } else {
    const { error } = await supabase.storage
      .from('documents')
      .upload(chemin, fichier, { contentType: 'application/pdf' })
    if (error) throw error
  }
  const octets = await fichier.arrayBuffer()
  try {
    await base.plans.put({ cle: chemin, octets, conserve_le: Date.now() })
  } catch {
    // appareil plein : il sera retéléchargé au besoin
  }
  const { data, error } = await supabase
    .from('documents')
    .insert({
      chantier_id: chantierId,
      type: infos.type,
      nom: fichier.name.replace(/\.pdf$/i, ''),
      fichier_path: chemin,
      taille_octets: fichier.size,
      importe_par: importePar,
      addenda_numero: infos.addenda_numero || null,
      date_emission: infos.date_emission || null,
      devis_id: infos.devis_id || null
    })
    .select()
    .single()
  if (error) throw error
  const doc = data as DocumentDevis
  const sansTexte = await preparerDocumentDevis(doc, progression, annule, fichier.name)
  return { doc, sansTexte }
}

/**
 * Lit le texte et découpe les pages d'un document pas encore prêt.
 * Reprend là où une préparation interrompue s'était arrêtée.
 * Retourne vrai si le PDF ne contient presque pas de texte (devis numérisé).
 */
export async function preparerDocumentDevis(
  doc: DocumentDevis,
  progression: (etape: Etape, fraction: number) => void,
  annule: () => boolean,
  nomFichier = doc.nom
): Promise<boolean> {
  const pdf = await ouvrirPlan(doc, (f) => progression('envoi', f))
  let sansTexte = false
  try {
    if (!doc.texte_extrait_le) {
      sansTexte = await extraireTexte(pdf, doc, nomFichier, (f) => progression('texte', f), annule)
      if (annule()) throw new Error('Préparation interrompue.')
      const maintenant = new Date().toISOString()
      const { error } = await supabase
        .from('documents')
        .update({ texte_extrait_le: maintenant, nb_pages: pdf.numPages })
        .eq('id', doc.id)
      if (error) throw error
      doc.texte_extrait_le = maintenant
      doc.nb_pages = pdf.numPages
    }
    if (!doc.pages_decoupees) {
      const octets = (await base.plans.get(doc.fichier_path))?.octets ?? (await pdf.getData()).slice().buffer
      progression('pages', 0)
      await decouperEtTeleverser(doc, octets as ArrayBuffer, (fait, total) => progression('pages', fait / total), annule)
      doc.pages_decoupees = true
    }
  } finally {
    void pdf.destroy()
  }
  return sansTexte
}

/** Lignes de texte d'une page, de haut en bas, avec leur position verticale. */
async function lignesDePage(page: PDFPageProxy): Promise<LignePage[]> {
  const vue = page.getViewport({ scale: 1 })
  const contenu = await page.getTextContent()
  const morceaux = contenu.items
    .filter((i): i is TextItem => 'str' in i && i.str.trim().length > 0)
    .map((i) => {
      const [x, y] = vue.convertToViewportPoint(i.transform[4], i.transform[5])
      const h = Math.hypot(i.transform[2], i.transform[3]) || 8
      return { x, y, h, l: i.width, s: i.str }
    })
    .sort((a, b) => a.y - b.y || a.x - b.x)

  const groupes: (typeof morceaux)[] = []
  for (const m of morceaux) {
    const g = groupes[groupes.length - 1]
    if (g && Math.abs(m.y - g[0].y) <= Math.max(2, Math.min(m.h, g[0].h) * 0.45)) g.push(m)
    else groupes.push([m])
  }
  return groupes.map((g) => {
    g.sort((a, b) => a.x - b.x)
    let texte = ''
    let finPrec = -Infinity
    for (const m of g) {
      if (texte && !texte.endsWith(' ') && !m.s.startsWith(' ') && m.x - finPrec > m.h * 0.2) texte += ' '
      texte += m.s
      finPrec = m.x + m.l
    }
    return { y: g[0].y / vue.height, texte }
  })
}

async function extraireTexte(
  pdf: PDFDocumentProxy,
  doc: DocumentDevis,
  nomFichier: string,
  progression: (f: number) => void,
  annule: () => boolean
): Promise<boolean> {
  const total = pdf.numPages
  const textes: string[] = []
  const sections: (SectionPage | null)[] = []
  let caracteres = 0
  for (let n = 1; n <= total; n++) {
    if (annule()) return false
    const page = await pdf.getPage(n)
    const lignes = await lignesDePage(page)
    page.cleanup()
    const texte = assemblerLignes(lignes.map((l) => l.texte))
    caracteres += texte.length
    textes.push(texte)
    sections.push(sectionDePage(lignes))
    if (n % 5 === 0 || n === total) progression(n / total)
  }
  const finales = sectionsDuDocument(sections, nomFichier)

  // Une nouvelle lecture ne touche pas aux sections corrigées à la main.
  const { data: manuelles } = await supabase
    .from('devis_pages')
    .select('page, section_code, section_titre')
    .eq('document_id', doc.id)
    .eq('section_source', 'manuel')
  const parPage = new Map((manuelles ?? []).map((m) => [m.page as number, m]))

  const rangs = textes.map((texte, i) => {
    const m = parPage.get(i + 1)
    return {
      document_id: doc.id,
      chantier_id: doc.chantier_id,
      page: i + 1,
      texte,
      texte_norm: normaliser(texte),
      section_code: m ? m.section_code : finales[i]?.code ?? null,
      section_titre: m ? m.section_titre : finales[i]?.titre ?? null,
      section_source: m ? 'manuel' : 'auto'
    }
  })
  for (let i = 0; i < rangs.length; i += 40) {
    if (annule()) return false
    const { error } = await supabase.from('devis_pages').upsert(rangs.slice(i, i + 40), { onConflict: 'document_id,page' })
    if (error) throw error
  }
  return caracteres / Math.max(1, total) < 30
}

